import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Sparkles, Sun, Moon, Heart, Compass, Anchor, Home, Mountain, Users } from "lucide-react";
import PricingCTA from "../components/PricingCTA";
import TopCitiesRow from "../components/TopCitiesRow";
import BlueprintBanner from "../components/BlueprintBanner";
import { log } from "../lib/log";

const ICONS = {
  Jupiter: Sparkles, Sun: Sun, Moon: Moon, Venus: Heart,
  AC: Anchor, IC: Home, MC: Mountain, DC: Users,
};

const KIND_LABEL = {
  planet: "Planetary Line",
  angle: "Angular Line",
};

function PlacePhoto({ city, planet, color }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    let created = null;
    (async () => {
      try {
        const res = await api.get(`/places/image?city=${encodeURIComponent(city)}&planet=${encodeURIComponent(planet)}`, { responseType: "blob" });
        const u = URL.createObjectURL(res.data);
        created = u;
        if (alive) setSrc(u);
      } catch {
        if (alive) setErr(true);
      }
    })();
    return () => { alive = false; if (created) URL.revokeObjectURL(created); };
  }, [city, planet]);

  return (
    <div className="mt-4 rounded-lg overflow-hidden relative aspect-[16/9] border border-white/10 bg-black/30" data-testid={`astro-photo-${planet}`}>
      {!src && !err && (
        <div className="absolute inset-0 flex items-center justify-center text-[10px] uppercase tracking-[0.25em]" style={{ color }}>
          Illustrating with Nano Banana…
        </div>
      )}
      {src && <img src={src} alt={`${planet} · ${city}`} className="w-full h-full object-cover" />}
      {err && <div className="absolute inset-0 flex items-center justify-center text-[10px] uppercase tracking-[0.2em] text-[#A89C98]">Vision unavailable</div>}
      <div className="absolute inset-0 pointer-events-none" style={{ background: `linear-gradient(180deg, transparent 40%, ${color}33)` }} />
    </div>
  );
}

function LineCard({ l, showPhoto }) {
  const nav = useNavigate();
  const Icon = ICONS[l.planet] || Compass;
  const isAngle = l.kind === "angle";
  return (
    <div
      className="glass rounded-2xl p-6 relative overflow-hidden hover:-translate-y-1 transition-transform"
      data-testid={`astro-line-${l.planet}`}
    >
      <div
        className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-30 blur-2xl"
        style={{ background: l.color }}
      />
      <div className="flex items-center gap-3 relative">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: `${l.color}22`, border: `1px solid ${l.color}66` }}
        >
          <Icon size={18} style={{ color: l.color }} />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#A89C98] flex items-center gap-2">
            <span>{l.planet} · {isAngle ? "Angle" : "Planet"}</span>
          </div>
          <div className="font-heading text-2xl mt-0.5 truncate">{l.city_display}</div>
        </div>
      </div>
      <p className="text-sm text-[#A89C98] mt-4 leading-relaxed relative">{l.meaning}</p>
      {l.country && (
        <div className="mt-4 relative">
          <div className="text-[9px] uppercase tracking-[0.28em] text-[#E8DFD3]/70 mb-2" data-testid={`activities-label-${l.planet}`}>
            In {l.country}, try
          </div>
          {Array.isArray(l.activities) && l.activities.length > 0 ? (
            <ul className="space-y-1.5" data-testid={`activities-list-${l.planet}`}>
              {l.activities.map((act) => (
                <li key={act} className="flex gap-2 text-[12px] leading-snug text-white/85">
                  <span className="text-[10px] mt-1 shrink-0" style={{ color: l.color }}>◆</span>
                  <span>{act}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-[11px] text-[#A89C98] italic">Local flavor coming soon.</div>
          )}
        </div>
      )}
      {showPhoto && <PlacePhoto city={l.city} planet={l.planet} color={l.color} />}
      <div className="mt-4 flex items-center justify-between relative">
        <div className="font-mono-scanner text-xs text-[#A89C98]">
          {l.lat.toFixed(2)}° {l.lat >= 0 ? "N" : "S"} · {Math.abs(l.lng).toFixed(2)}° {l.lng >= 0 ? "E" : "W"}
        </div>
        <button
          data-testid={`astro-line-${l.planet}-hub`}
          onClick={() => nav(`/chat/hub/${encodeURIComponent(l.city)}`)}
          className="text-[10px] uppercase tracking-[0.2em] hover:text-white transition-colors"
          style={{ color: l.color }}
        >
          Join Hub →
        </button>
      </div>
    </div>
  );
}

export default function NatalReveal() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/users/astrocartography");
        setLines(res.data.lines);
      } catch (e) {
        log.error("NatalReveal: astrocartography fetch failed:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const planetary = lines.filter((l) => l.kind === "planet" || !l.kind);
  const angular = lines.filter((l) => l.kind === "angle");


  return (
    <div className="min-h-screen relative bg-[#15111A]">
      <div className="starfield" />
      <div className="noise" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-8 pt-16 pb-24">
        <p className="text-xs uppercase tracking-[0.3em] text-[#A89C98]">Step 2 · Astrocartography Reveal</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter mt-3" data-testid="reveal-title">
          Your <span className="text-[#C8A96E]">planetary</span> & <span className="text-[#38BDF8]">angular</span> lines
        </h1>
        <p className="text-[#A89C98] mt-4 max-w-2xl">
          {user?.natal?.zodiac ? `${user.natal.zodiac} · ` : ""}
          Where your chart's most benevolent planets and its four cardinal angles — Ascendant, Imum Coeli,
          Midheaven and Descendant — cross the Earth. Visit these cities to activate their power.
        </p>

        <BlueprintBanner className="mt-8" testid="blueprint-cta" />

        {loading ? (
          <div className="mt-16 text-[#A89C98]" data-testid="reveal-loading">Calibrating your cartography…</div>
        ) : (
          <>
            <div className="mt-10">
              <TopCitiesRow />
            </div>
            {planetary.length > 0 && (
              <section className="mt-14" data-testid="planetary-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#C8A96E]/40 to-transparent" />
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96E]">Planetary Lines</div>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#C8A96E]/40 to-transparent" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5" data-testid="astro-lines-grid">
                  {planetary.map((l) => (
                    <LineCard key={l.planet} l={l} showPhoto />
                  ))}
                </div>
              </section>
            )}

            {angular.length > 0 && (
              <section className="mt-14" data-testid="angular-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#38BDF8]/40 to-transparent" />
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#38BDF8]">Angular Lines · AC / IC / MC / DC</div>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#38BDF8]/40 to-transparent" />
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="angular-lines-grid">
                  {angular.map((l) => (
                    <LineCard key={l.planet} l={l} showPhoto={false} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {/* Unlock the full experience */}
        <section className="mt-16" data-testid="reveal-pricing-cta">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#C8A96E]/40 to-transparent" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96E]">Unlock the full journey</div>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#C8A96E]/40 to-transparent" />
          </div>
          <PricingCTA compact />
        </section>

        <div className="mt-12 flex flex-wrap gap-4">
          <Button
            data-testid="reveal-continue-btn"
            onClick={() => nav("/dashboard", { replace: true })}
            className="bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none h-12 px-8 uppercase tracking-widest"
          >
            Enter Dashboard
          </Button>
          <Button
            data-testid="reveal-scanner-btn"
            variant="outline"
            onClick={() => nav("/scanner")}
            className="border-[#4A3356] bg-transparent text-white hover:bg-[#4A3356]/20 rounded-none h-12 px-8 uppercase tracking-widest"
          >
            Open Scanner
          </Button>
        </div>
      </div>
    </div>
  );
}
